/**
 * Deterministic company-name equivalence for worksheet verification tasks.
 *
 * A worksheet states a claimed company in one form ("Alignment Health, USA")
 * while FactSet resolves the same company in another ("Alignment Healthcare,
 * Inc."). Judging those pairs from a replayed transcript is unreliable, so the
 * comparison is reduced here to canonical keys plus a small set of relations.
 */

export type CompanyNameRelation = "exact" | "variation" | "different";

export interface CanonicalCompanyName {
  /** Canonical significant tokens joined by single spaces. */
  readonly key: string;
  /** `key` with German vowel digraphs folded (Guettinger -> Guttinger). */
  readonly folded: string;
  readonly tokens: readonly string[];
  /** First letters of the significant tokens, for acronym matching. */
  readonly initials: string;
  /** Canonical keys of parenthetical aliases, such as "(BBVA)". */
  readonly aliases: readonly string[];
}

export interface CompanyNameComparison {
  readonly relation: CompanyNameRelation;
  readonly left: CanonicalCompanyName;
  readonly right: CanonicalCompanyName;
}

/** Multi-word legal designators, removed before tokens are filtered. */
const LEGAL_PHRASES: readonly string[] = [
  "societe par actions simplifiee",
  "societe en commandite par actions",
  "societe anonyme",
  "public limited company",
  "private limited company",
  "limited liability company",
  "limited liability partnership",
  "limited partnership",
  "gesellschaft mit beschrankter haftung",
  "kommanditgesellschaft auf aktien",
  "sociedad anonima",
  "sociedade anonima",
  "societa per azioni",
  "naamloze vennootschap",
  "besloten vennootschap",
  "kabushiki kaisha",
  "proprietary limited",
  "private limited",
];

/** Single-token legal designators across the jurisdictions FactSet returns. */
const LEGAL_TOKENS = new Set([
  "ab",
  "ad",
  "ag",
  "aktiengesellschaft",
  "aps",
  "as",
  "asa",
  "bhd",
  "bv",
  "cia",
  "cie",
  "co",
  "companies",
  "company",
  "corp",
  "corporation",
  "cv",
  "dd",
  "doo",
  "ehf",
  "eurl",
  "gk",
  "gmbh",
  "hf",
  "inc",
  "incorporated",
  "jsc",
  "kg",
  "kgaa",
  "kk",
  "limited",
  "llc",
  "llp",
  "lp",
  "ltd",
  "ltda",
  "mbh",
  "nv",
  "oao",
  "ojsc",
  "oy",
  "oyj",
  "pao",
  "pjsc",
  "plc",
  "private",
  "proprietary",
  "pt",
  "pty",
  "pvt",
  "sa",
  "sab",
  "sarl",
  "sas",
  "sasu",
  "sca",
  "sdn",
  "se",
  "sl",
  "snc",
  "spa",
  "srl",
  "tbk",
  "zao",
  "zoo",
]);

/** Tokens that describe corporate structure rather than identity. */
const STRUCTURAL_TOKENS = new Set([
  "groupe",
  "gruppe",
  "grupo",
  "group",
  "holdco",
  "holding",
  "holdings",
  "the",
]);

/** Connectives skipped when deriving acronym initials. */
const INITIAL_STOPWORDS = new Set([
  "and",
  "da",
  "das",
  "de",
  "del",
  "den",
  "der",
  "di",
  "do",
  "du",
  "el",
  "en",
  "et",
  "la",
  "le",
  "les",
  "of",
  "und",
  "van",
  "von",
  "y",
]);

/**
 * Words too generic to carry identity on their own. A containment match needs
 * at least one token outside this set so "Capital" does not match every fund.
 */
const GENERIC_TOKENS = new Set([
  "bank",
  "banking",
  "capital",
  "energy",
  "equity",
  "financial",
  "fund",
  "industries",
  "international",
  "investment",
  "investments",
  "management",
  "partners",
  "services",
  "solutions",
  "systems",
  "technologies",
  "technology",
  "ventures",
]);

/** Parenthetical markers that never denote an alias. */
const MARKER_ALIASES = new Set(["br", "cn", "jp", "uk", "up", "us", "usa"]);

const DIACRITIC_REPLACEMENTS: ReadonlyArray<readonly [RegExp, string]> = [
  [/ß/g, "ss"],
  [/[øŐ]/gi, "o"],
  [/æ/gi, "ae"],
  [/œ/gi, "oe"],
  [/đ|ð/gi, "d"],
  [/ł/gi, "l"],
  [/ı/gi, "i"],
  [/þ/gi, "th"],
];

/** Joins the letters of dotted acronyms: `S.A.B.` -> `SAB`. */
function collapseDottedAcronyms(value: string): string {
  return value.replace(/(?:[A-Za-z]\.){2,}/g, (match) =>
    match.replace(/\./g, ""),
  );
}

function stripDiacritics(value: string): string {
  const replaced = DIACRITIC_REPLACEMENTS.reduce(
    (text, [pattern, replacement]) => text.replace(pattern, replacement),
    value,
  );
  return replaced.normalize("NFKD").replace(/[\u0300-\u036f]/g, "");
}

function tokenize(value: string): string[] {
  const flattened = stripDiacritics(collapseDottedAcronyms(value))
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
  if (!flattened) return [];
  const withoutPhrases = LEGAL_PHRASES.reduce(
    (text, phrase) => text.split(` ${phrase} `).join(" "),
    ` ${flattened} `,
  );
  return withoutPhrases
    .split(/\s+/)
    .filter(
      (token) =>
        token.length > 1 &&
        !LEGAL_TOKENS.has(token) &&
        !STRUCTURAL_TOKENS.has(token),
    );
}

/** Folds the German vowel digraphs that survive diacritic stripping. */
function foldVowelDigraphs(value: string): string {
  return value.replace(/ae/g, "a").replace(/oe/g, "o").replace(/ue/g, "u");
}

/**
 * Reduces a worksheet company name to comparable form by dropping annotations
 * ("(UP)", "/Private Group/"), a trailing country or legal segment, accents,
 * punctuation, and legal or structural designators.
 */
export function canonicalizeCompanyName(value: unknown): CanonicalCompanyName {
  const aliasSources: string[] = [];
  let text = String(value ?? "")
    .normalize("NFKC")
    .replace(/\(([^)]*)\)/g, (_match, inner: string) => {
      aliasSources.push(inner);
      return " ";
    })
    .replace(/\/[^/]*\//g, " ")
    .replace(/\/[^/]*$/, " ")
    .replace(/\[[^\]]*\]/g, " ");
  const lastComma = text.lastIndexOf(",");
  if (lastComma > 0) text = text.slice(0, lastComma);
  const tokens = tokenize(text);
  const key = tokens.join(" ");
  const aliases = aliasSources
    .map((alias) => tokenize(alias).join(" "))
    .filter((alias) => alias.length > 1 && !MARKER_ALIASES.has(alias));
  return {
    key,
    folded: foldVowelDigraphs(key),
    tokens,
    initials: tokens
      .filter((token) => !INITIAL_STOPWORDS.has(token))
      .map((token) => token.charAt(0))
      .join(""),
    aliases,
  };
}

function hasDistinctiveToken(tokens: readonly string[]): boolean {
  return tokens.some(
    (token) => token.length >= 3 && !GENERIC_TOKENS.has(token),
  );
}

/**
 * True when the shorter key opens the longer one, either at a word boundary
 * ("Alkami" / "Alkami Technology") or inside a shared long word ("Alignment
 * Health" / "Alignment Healthcare").
 */
function isPrefixVariation(
  left: CanonicalCompanyName,
  right: CanonicalCompanyName,
): boolean {
  const [shorter, longer] =
    left.folded.length <= right.folded.length ? [left, right] : [right, left];
  if (
    shorter.folded.length < 4 ||
    !hasDistinctiveToken(shorter.tokens) ||
    !longer.folded.startsWith(shorter.folded)
  ) {
    return false;
  }
  if (longer.folded.charAt(shorter.folded.length) === " ") return true;
  const lastToken = shorter.folded.slice(
    shorter.folded.lastIndexOf(" ") + 1,
  );
  return lastToken.length >= 4;
}

/** True when every token of one side also appears on the other side. */
function isTokenSubset(
  left: readonly string[],
  right: readonly string[],
): boolean {
  const [shorter, longer] =
    left.length <= right.length ? [left, right] : [right, left];
  if (shorter.length === 0 || !hasDistinctiveToken(shorter)) return false;
  const longerSet = new Set(longer);
  return shorter.every((token) => longerSet.has(token));
}

/** True when one side is an acronym or parenthetical alias of the other. */
function isAliasVariation(
  left: CanonicalCompanyName,
  right: CanonicalCompanyName,
): boolean {
  const acronymMatch = (
    candidate: CanonicalCompanyName,
    expanded: CanonicalCompanyName,
  ): boolean =>
    candidate.tokens.length === 1 &&
    candidate.key.length >= 3 &&
    candidate.key === expanded.initials;
  if (acronymMatch(left, right) || acronymMatch(right, left)) return true;
  const aliasMatch = (
    candidate: CanonicalCompanyName,
    other: CanonicalCompanyName,
  ): boolean =>
    candidate.aliases.some(
      (alias) =>
        alias === other.key ||
        alias === other.folded ||
        (alias.length >= 3 && alias === other.initials),
    );
  return aliasMatch(left, right) || aliasMatch(right, left);
}

/**
 * Classifies two worksheet company names as the same company written
 * identically, the same company written differently, or different companies.
 */
export function compareCompanyNames(
  left: unknown,
  right: unknown,
): CompanyNameComparison {
  const leftName = canonicalizeCompanyName(left);
  const rightName = canonicalizeCompanyName(right);
  const relation = ((): CompanyNameRelation => {
    if (!leftName.key || !rightName.key) return "different";
    if (leftName.key === rightName.key) return "exact";
    if (
      leftName.folded === rightName.folded ||
      isPrefixVariation(leftName, rightName) ||
      isTokenSubset(leftName.tokens, rightName.tokens) ||
      isAliasVariation(leftName, rightName)
    ) {
      return "variation";
    }
    return "different";
  })();
  return { relation, left: leftName, right: rightName };
}

/** True when both names denote the same company. */
export function companyNamesMatch(left: unknown, right: unknown): boolean {
  return compareCompanyNames(left, right).relation !== "different";
}
